import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import accueilVanPlage from '../assets/images/location-van-amenage-plage-to-the-road.jpg';
import accueilVanMontagne from '../assets/images/location-van-amenage-montagne-to-the-road.jpg';

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "To The Road",
  "description": "Loueur de vans aménagés Volkswagen au départ de Versailles. Réservation via Wikicampers, Yescapa, Getaround, Roadstr et Turo.",
  "url": "https://totheroad.fr",
  "logo": "https://totheroad.fr/logo_couleur_orange_512x512.png",
  "image": "https://totheroad.fr/og-image.jpg",
  "email": "contact@totheroad.fr",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Versailles",
    "addressRegion": "Île-de-France",
    "addressCountry": "FR"
  },
  "areaServed": {
    "@type": "State",
    "name": "Île-de-France"
  },
  "sameAs": [
    "https://www.instagram.com/2.the.road"
  ]
};

const Home: React.FC = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Location de vans aménagés à Versailles — To The Road</title>
        <meta name="description" content="Louez un van aménagé Volkswagen California ou ID. Buzz au départ de Versailles. Réservez sur Wikicampers, Yescapa ou Getaround. Liberté et confort garantis." />
        <link rel="canonical" href="https://totheroad.fr/" />
        <meta property="og:title" content="Location de vans aménagés à Versailles — To The Road" />
        <meta property="og:description" content="Louez un van aménagé Volkswagen California ou ID. Buzz au départ de Versailles. Réservez sur Wikicampers, Yescapa ou Getaround. Liberté et confort garantis." />
        <meta property="og:image" content="https://totheroad.fr/og-image.jpg" />
        <meta property="og:url" content="https://totheroad.fr/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>
      {/* Hero Section - Van on Beach */}
      <section
        className="relative min-h-screen flex items-center justify-center [background-position:25%_center] md:[background-position:center]"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${accueilVanPlage})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="text-center text-white z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
            Partez à l'aventure
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl mb-8 max-w-2xl mx-auto">
            Liberté, confort et moments inoubliables en famille ou entre amis.
          </p>
          <Link to="/reserver" 
          className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors duration-200 shadow-lg">
            C'est parti
          </Link>
        </div>
      </section>

      {/* SECTION VISION */}
      <section className="pt-16 pb-2 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 font-quicksand">
            Une autre façon de voyager
          </h2>
          <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
            <p>
              Chez To The Road, chaque voyage est unique.<br /> Nous vous proposons des véhicules qui s'adaptent à votre histoire.
            </p>
            <p>
              Un week-end en famille ? Un week-end en amoureux ?{' '}<br />
              Un road trip estival ? Une escapade entre amis ?
            </p>
            <p className="text-xl font-semibold text-blue-600">
              Nous trouvons le véhicule qui vous correspond.
            </p>
          </div>
        </div>
      </section>

      {/* Road Separator */}
      <div className="w-full overflow-hidden bg-white" style={{ lineHeight: 0 }}>
        <svg
          viewBox="0 0 1400 240"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full"
          style={{ display: 'block' }}
        >
          {/* Grey asphalt band */}
          <path d="M -100,150 C 220,150 360,72 570,106 S 860,190 1060,152 S 1260,76 1500,104"
            fill="none" stroke="#888" strokeWidth="100" strokeLinecap="butt" />
          {/* White continuous top edge line (offset -44 from center) */}
          <path d="M -100,106 C 220,106 360,28 570,62 S 860,146 1060,108 S 1260,32 1500,60"
            fill="none" stroke="white" strokeWidth="5" strokeLinecap="butt" />
          {/* White continuous bottom edge line (offset +44 from center) */}
          <path d="M -100,194 C 220,194 360,116 570,150 S 860,234 1060,196 S 1260,120 1500,148"
            fill="none" stroke="white" strokeWidth="5" strokeLinecap="butt" />
          {/* White dashed center line */}
          <path d="M -100,150 C 220,150 360,72 570,106 S 860,190 1060,152 S 1260,76 1500,104"
            fill="none" stroke="white" strokeWidth="4"
            strokeDasharray="42 26" strokeLinecap="butt" />
        </svg>
      </div>

      {/* Des souvenirs à chaque voyage Section */}
      <section className="pt-4 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-500 mb-8">
              Des souvenirs à chaque voyage
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {/* Liberté Totale */}
            <div className="bg-white rounded-xl p-8 shadow-2xl hover:shadow-xl transform hover:scale-105 transition-all duration-300 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Liberté Totale</h3>
              <p className="text-gray-600 leading-relaxed">
                Créez votre itinéraire, changez de destination selon vos envies et explorez sans contraintes.
              </p>
            </div>

            {/* Confort Familial */}
            <div className="bg-white rounded-xl p-8 shadow-2xl hover:shadow-xl transform hover:scale-105 transition-all duration-300 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Confort Familial</h3>
              <p className="text-gray-600 leading-relaxed">
                Un espace optimisé pour 2 à 4 personnes, avec tout le confort nécessaire pour petits et grands.
              </p>
            </div>

            {/* Aventure Assurée */}
            <div className="bg-white rounded-xl p-8 shadow-2xl hover:shadow-xl transform hover:scale-105 transition-all duration-300 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Aventure Assurée</h3>
              <p className="text-gray-600 leading-relaxed">
                Plein équipements inclus pour vos road trips, des montagnes aux plages ensoleillées.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Votre Maison sur Roues Section */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-blue-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <div className="w-[75%] mx-auto rounded-lg overflow-hidden shadow-lg">
                <img 
                  src={accueilVanMontagne}
                  alt="Location van aménagé en montagne - To The Road"
                  className="w-full h-auto"
                />
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Votre Maison sur Roues
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Vous en rêvez depuis longtemps ? C'est le moment ! Partez à l'aventure sans compromis : 
                tout le confort d'une maison, toute la liberté du voyage. Votre van life commence maintenant.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center mr-3">
                    <span className="text-2xl">🎯</span>
                  </div>
                  <span className="text-gray-700">Enfin, passez à l'action</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center mr-3">
                    <span className="text-2xl">🏕️</span>
                  </div>
                  <span className="text-gray-700">Campez où bon vous semble</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center mr-3">
                    <span className="text-2xl">📷</span>
                  </div>
                  <span className="text-gray-700">Des photos de ouf garanties</span>
                </div>
              </div>

              <Link to="/van-life" className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors duration-200 shadow-lg">
                Découvrez la vie autrement
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;